import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import HttpClient from '../../Api/HttpClient';
import handle401 from '../handle-401';

function transformApiPlaybookToUi(playbook) {
  return {
    id: playbook.id,
    name: playbook.name,
    description: playbook.description,
    lastActivity: playbook.last_activity_dt,
    priority: playbook.priority,
    duration: playbook.project_duration,
    averageValue: playbook.project_value,
    timesUsed: playbook.times_used_as_template,
    cardsCount: playbook.total_cards,
    documentsCount: playbook.board_document_count,
    archived: playbook.archived,
  };
}

export function transformCardToUi(card) {
  return {
    id: card.id,
    name: card.title,
    startDate: card.start_date,
    endDate: card.end_date,
    taskTypeLabel: card.task_type_details?.custom_label || 'unknown',
    taskType: {
      id: card.task_type_details?.id,
      label: card.task_type_details?.custom_label,
      color: card.task_type_details?.color,
      lastCompletionTime: card.task_type_details?.last_completion_time,
      averageCompletionTime: card.task_type_details?.last5_avg_completion_time,
    },
    duration: card.duration,
    offset: card.offset,
    documentsCount: card.document_count,
    description: card.description,
    weightPercentage: card.weight_percentage,
    weight: card.weight,
    stepsCount: card.steps_count,
    playbookOwnerAssigned: card.template_owner_assigned,
    defaultAssignee: card.internal_assignee_details
      ? `${card.internal_assignee_details.first_name} ${card.internal_assignee_details.last_name}`
      : '<Unassigned>',
    timing: card.task_timing,
    clientVisible: card.client_visible,
    referencedFlowName: card.ref_task_name,
    referencedFlowId: card.ref_task_id,
  };
}

export function transformCardGroupToUI(group, cardTransformFn = transformCardToUi) {
  const { subtasks, stepsCount, documentsCount, duration, weight } = group.sub_tasks.reduce(
    (acc, card) => {
      return {
        documentsCount: acc.documentsCount + card.document_count,
        stepsCount: acc.stepsCount + card.steps_count,
        duration: acc.duration + card.duration,
        weight: acc.weight + card.weight,
        subtasks: [...acc.subtasks, cardTransformFn(card)],
      };
    },
    {
      documentsCount: 0,
      stepsCount: 0,
      duration: 0,
      weight: 0,
      subtasks: [],
    },
  );

  return {
    id: group.id,
    name: group.title,
    documentsCount,
    stepsCount,
    taskTypeLabel: '<Group>',
    isTaskGroup: true,
    timing: group.task_timing,
    referencedFlowName: group.ref_task_name,
    referencedFlowId: group.ref_task_id,
    offset: group.offset,
    duration,
    weight,
    subtasks: subtasks.sort((a, b) => a.startDate.localeCompare(b.startDate)),
    startDate: group.start_date,
    endDate: group.end_date,
    isGroup: true,
  };
}

function transformAPIPlaybookToUI(playbook) {
  const singleTasks = playbook.standalone_cards.map((card) => transformCardToUi(card));
  const groups = playbook.grouped_cards.map((group) => transformCardGroupToUI(group));

  return {
    id: playbook.id,
    name: playbook.name,
    timesUsed: playbook.times_used_as_template,
    lastActivity: playbook.last_activity_dt,
    priority: playbook.priority,
    duration: playbook.project_duration,
    averageValue: playbook.project_value,
    cardsCount: playbook.total_cards,
    documentsCount: playbook.board_document_count,
    projectType: playbook.project_type,
    description: playbook.description,
    isDescriptionHtml: playbook.description_is_html,
    tasks: [...singleTasks, ...groups],
  };
}

export function playbooksListQuery({ archived = false } = {}) {
  return queryOptions({
    gcTime: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    queryKey: ['playbooks-list', archived],
    queryFn: handle401(async () => {
      const { data } = await HttpClient.getPlaybooks({ archived });

      return data.map((playbook) => transformApiPlaybookToUi(playbook));
    }),
  });
}

export function playbookQuery({ playbookId, groupView }) {
  return queryOptions({
    staleTime: 5 * 60 * 1000,
    queryKey: ['playbook', playbookId, groupView],
    queryFn: handle401(async () => {
      const { data } = await HttpClient.getPlaybook({ id: playbookId, groupView });

      return transformAPIPlaybookToUI(data[0]);
    }),
  });
}
